import { useNavigate } from "react-router-dom";
import { FC, useCallback } from "react";
import {
  Button,
  Container,
  Form,
  FormField,
  FormButtons,
  notification,
  useCall,
} from "@epcnetwork/core-ui-kit";

import { getPipelineRoute } from "utils";
import { JOBS_LIST_PAGE } from "constants/routes.constants";
import { postOptizmoJob } from "api";
import { PipelineHeaderSection } from "../components/pipeline-header-section/pipeline-header-section";
import { OptizmoFormValues, OptizmoPayload } from "./optizmo.types";
import { initialValues, parseClientGroups, validationSchema } from "./optizmo.constants";

import styles from "./optizmo.module.css";

const PLACEHOLDER = "Golden Crest, key1, key2\nMotley Fool, key1, key2";

const OptizmoPage: FC = () => {
  const navigate = useNavigate();
  const pipeline = getPipelineRoute("Optizmo");

  const { submit, submitting, onCallSuccess, onCallError } = useCall(postOptizmoJob);
  onCallSuccess(() => {
    notification.success("Optizmo job created", "Job has been queued for processing.");
    navigate(JOBS_LIST_PAGE.path);
  });
  onCallError((error) => {
    notification.error("Failed to create job", error.message);
  });

  const handleSubmit = useCallback(
    async (values: OptizmoFormValues) => {
      const clientGroups = parseClientGroups(values.clientGroupsRaw);

      const payload: OptizmoPayload = { apiKey: values.apiKey, clientGroups };

      await submit({ data: payload });
    },
    [submit],
  );

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Container>
      <div className={styles.container}>
        <PipelineHeaderSection pipeline={pipeline} />

        <div className={styles.form}>
          <Form
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            <div className={styles.fieldGroup}>
              <FormField
                type="password"
                name="apiKey"
                label="Affiliate Token"
                placeholder="Affiliate token from your Optizmo account settings"
              />
              <p className={styles.hint}>Affiliate token from your Optizmo account settings.</p>
            </div>

            <div className={styles.fieldGroup}>
              <FormField
                type="textarea"
                name="clientGroupsRaw"
                label="Client Groups"
                placeholder={PLACEHOLDER}
                className={styles.textarea}
              />
              <p className={styles.hint}>
                One line per client: Client Name, key1, key2. Client names must not contain commas.
              </p>
            </div>

            <FormButtons>
              <Button appearance="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                Submit
              </Button>
            </FormButtons>
          </Form>
        </div>
      </div>
    </Container>
  );
};

export { OptizmoPage };
